import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Users, Shield, Car, Store, UserX, Loader2, Phone, Mail, IdCard, Calendar, Clock, X, ChevronRight } from 'lucide-react';
interface UserSummary {
  user_id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  national_id: string | null;
  profile_is_active: boolean;
  is_platform_admin: boolean;
  platform_role: string | null;
  is_driver: boolean;
  driver_id: number | null;
  has_restaurant_access: boolean;
  restaurant_access_count: number;
  restaurant_roles: string[] | null;
  auth_created_at: string;
  last_sign_in_at: string | null;
}
export function UsersAdminPage() {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Filters
  const [filterAdmin, setFilterAdmin] = useState(false);
  const [filterDriver, setFilterDriver] = useState(false);
  const [filterRestaurant, setFilterRestaurant] = useState(false);
  const [filterInactive, setFilterInactive] = useState(false);
  // Drawer state
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  useEffect(() => {
    fetchUsers();
  }, []);
  const fetchUsers = async () => {
    setLoading(true);
    const {
      data,
      error
    } = await supabase.from('v_user_access_summary').select('*').order('auth_created_at', {
      ascending: false
    });
    if (!error && data) {
      setUsers(data);
    } else {
      console.error('Error fetching users:', error);
    }
    setLoading(false);
  };
  // Apply filters and search
  const filteredUsers = users.filter(u => {
    // Search
    const s = search.toLowerCase();
    const matchesSearch = !s || u.full_name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.phone?.includes(s) || u.national_id?.includes(s);
    if (!matchesSearch) return false;
    // Toggles (if any toggle is active, user must match ALL active toggles)
    if (filterAdmin && !u.is_platform_admin) return false;
    if (filterDriver && !u.is_driver) return false;
    if (filterRestaurant && !u.has_restaurant_access) return false;
    if (filterInactive && u.profile_is_active) return false;
    return true;
  });
  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Nunca';
    return new Date(isoString).toLocaleDateString('es-CR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex" data-id="element-6350">
      <div className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-6351">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-6352">
          <div className="w-12 h-12 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-6353">
            <Users className="w-6 h-6 text-[#006847]" data-id="element-6354" />
          </div>
          <div data-id="element-6355">
            <h1 className="text-2xl font-bold" data-id="element-6356">Usuarios del Sistema</h1>
            <p className="text-white/40 text-sm" data-id="element-6357">
              {filteredUsers.length}{' '}
              {filteredUsers.length === 1 ? 'usuario' : 'usuarios'} encontrados
            </p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="space-y-4" data-id="element-6358">
          <div className="relative" data-id="element-6359">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-6360" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, email, teléfono o cédula..." className="w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-6361" />
          </div>

          <div className="flex flex-wrap gap-2" data-id="element-6362">
            <button onClick={() => setFilterAdmin(!filterAdmin)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterAdmin ? 'bg-[#006847] text-white' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6363">
              <Shield className="w-3.5 h-3.5" data-id="element-6364" /> Admin NOIZA
            </button>
            <button onClick={() => setFilterDriver(!filterDriver)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterDriver ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6365">
              <Car className="w-3.5 h-3.5" data-id="element-6366" /> Conductores
            </button>
            <button onClick={() => setFilterRestaurant(!filterRestaurant)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterRestaurant ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6367">
              <Store className="w-3.5 h-3.5" data-id="element-6368" /> Restaurante
            </button>
            <button onClick={() => setFilterInactive(!filterInactive)} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${filterInactive ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-[#141414] border border-white/10 text-white/50 hover:bg-white/5'}`} data-id="element-6369">
              <UserX className="w-3.5 h-3.5" data-id="element-6370" /> Inactivos
            </button>
          </div>
        </div>

        {/* List */}
        {loading ? <div className="flex justify-center py-20" data-id="element-6371">
            <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-6372" />
          </div> : filteredUsers.length === 0 ? <div className="text-center py-20 bg-[#141414] border border-white/10 rounded-2xl" data-id="element-6373">
            <Users className="w-12 h-12 text-white/10 mx-auto mb-4" data-id="element-6374" />
            <p className="text-white/40 text-lg font-medium" data-id="element-6375">
              No se encontraron usuarios
            </p>
          </div> : <div className="grid gap-3" data-id="element-6376">
            {filteredUsers.map(user => <div key={user.user_id} onClick={() => setSelectedUser(user)} className="bg-[#141414] border border-white/10 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-colors group" data-id="element-6377">
                {/* Avatar */}
                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center shrink-0 text-xl font-black text-white/30" data-id="element-6378">
                  {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" data-id="element-6379">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6380">
                    <h3 className="font-bold text-white truncate" data-id="element-6381">
                      {user.full_name || 'Sin nombre'}
                    </h3>
                    {!user.profile_is_active && <span className="bg-red-500/10 text-red-400 text-[10px] uppercase font-black px-2 py-0.5 rounded-md shrink-0" data-id="element-6382">
                        Inactivo
                      </span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/50 truncate" data-id="element-6383">
                    <span className="truncate" data-id="element-6384">{user.email}</span>
                    {user.phone && <>
                        <span className="w-1 h-1 bg-white/20 rounded-full shrink-0" data-id="element-6385" />
                        <span className="shrink-0" data-id="element-6386">{user.phone}</span>
                      </>}
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mt-2" data-id="element-6387">
                    {user.is_platform_admin && <span className="inline-flex items-center gap-1 bg-[#006847]/20 text-[#00a870] text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6388">
                        <Shield className="w-3 h-3" data-id="element-6389" /> Admin NOIZA
                      </span>}
                    {user.is_driver && <span className="inline-flex items-center gap-1 bg-blue-500/10 text-blue-400 text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6390">
                        <Car className="w-3 h-3" data-id="element-6391" /> Conductor
                      </span>}
                    {user.has_restaurant_access && <span className="inline-flex items-center gap-1 bg-purple-500/10 text-purple-400 text-xs font-bold px-2 py-0.5 rounded-md" data-id="element-6392">
                        <Store className="w-3 h-3" data-id="element-6393" /> Restaurante (
                        {user.restaurant_access_count})
                      </span>}
                  </div>
                </div>

                <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/50 transition-colors shrink-0" data-id="element-6394" />
              </div>)}
          </div>}
      </div>

      {/* Detail Drawer */}
      {selectedUser && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setSelectedUser(null)} data-id="element-6395" />

          {/* Drawer */}
          <div className="fixed inset-y-0 right-0 w-full max-w-md bg-[#141414] border-l border-white/10 z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-200" data-id="element-6396">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-6397">
              <h2 className="text-lg font-bold" data-id="element-6398">Detalles del Usuario</h2>
              <button onClick={() => setSelectedUser(null)} className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-id="element-6399">
                <X className="w-4 h-4" data-id="element-6400" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-6401">
              {/* Profile Header */}
              <div className="flex items-center gap-4" data-id="element-6402">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center text-2xl font-black text-white/30" data-id="element-6403">
                  {selectedUser.full_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div data-id="element-6404">
                  <h3 className="text-xl font-bold text-white" data-id="element-6405">
                    {selectedUser.full_name || 'Sin nombre'}
                  </h3>
                  <p className="text-white/50 text-sm" data-id="element-6406">{selectedUser.email}</p>
                </div>
              </div>

              {/* Roles Summary */}
              <div className="space-y-3" data-id="element-6407">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6408">
                  Roles en la plataforma
                </h4>
                <div className="grid gap-2" data-id="element-6409">
                  {selectedUser.is_platform_admin && <div className="bg-[#006847]/10 border border-[#006847]/20 rounded-xl p-3 flex items-center gap-3" data-id="element-6410">
                      <div className="w-8 h-8 bg-[#006847]/20 rounded-lg flex items-center justify-center" data-id="element-6411">
                        <Shield className="w-4 h-4 text-[#00a870]" data-id="element-6412" />
                      </div>
                      <div data-id="element-6413">
                        <p className="font-bold text-[#00a870] text-sm" data-id="element-6414">
                          Admin NOIZA
                        </p>
                        <p className="text-xs text-[#00a870]/70" data-id="element-6415">
                          {selectedUser.platform_role || 'Administrador'}
                        </p>
                      </div>
                    </div>}

                  {selectedUser.is_driver && <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 flex items-center gap-3" data-id="element-6416">
                      <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center" data-id="element-6417">
                        <Car className="w-4 h-4 text-blue-400" data-id="element-6418" />
                      </div>
                      <div className="flex-1" data-id="element-6419">
                        <p className="font-bold text-blue-400 text-sm" data-id="element-6420">
                          Conductor
                        </p>
                        <p className="text-xs text-blue-400/70" data-id="element-6421">
                          ID: {selectedUser.driver_id}
                        </p>
                      </div>
                    </div>}

                  {selectedUser.has_restaurant_access && <div className="bg-purple-500/5 border border-purple-500/10 rounded-xl p-3 flex items-center gap-3" data-id="element-6422">
                      <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center" data-id="element-6423">
                        <Store className="w-4 h-4 text-purple-400" data-id="element-6424" />
                      </div>
                      <div data-id="element-6425">
                        <p className="font-bold text-purple-400 text-sm" data-id="element-6426">
                          Acceso a Restaurantes
                        </p>
                        <p className="text-xs text-purple-400/70" data-id="element-6427">
                          {selectedUser.restaurant_access_count} restaurante(s)
                        </p>
                      </div>
                    </div>}

                  {!selectedUser.is_platform_admin && !selectedUser.is_driver && !selectedUser.has_restaurant_access && <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center" data-id="element-6428">
                        <p className="text-sm text-white/40" data-id="element-6429">
                          Sin roles asignados
                        </p>
                      </div>}
                </div>
              </div>

              {/* Personal Info */}
              <div className="space-y-3" data-id="element-6430">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6431">
                  Información Personal
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4" data-id="element-6432">
                  <div className="flex items-center gap-3" data-id="element-6433">
                    <Phone className="w-4 h-4 text-white/30" data-id="element-6434" />
                    <div data-id="element-6435">
                      <p className="text-xs text-white/40" data-id="element-6436">Teléfono</p>
                      <p className="text-sm font-medium" data-id="element-6437">
                        {selectedUser.phone || 'No registrado'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6438">
                    <IdCard className="w-4 h-4 text-white/30" data-id="element-6439" />
                    <div data-id="element-6440">
                      <p className="text-xs text-white/40" data-id="element-6441">Cédula</p>
                      <p className="text-sm font-medium" data-id="element-6442">
                        {selectedUser.national_id || 'No registrada'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6443">
                    <UserX className="w-4 h-4 text-white/30" data-id="element-6444" />
                    <div data-id="element-6445">
                      <p className="text-xs text-white/40" data-id="element-6446">Estado del perfil</p>
                      <p className={`text-sm font-bold ${selectedUser.profile_is_active ? 'text-[#00a870]' : 'text-red-400'}`} data-id="element-6447">
                        {selectedUser.profile_is_active ? 'Activo' : 'Inactivo'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Info */}
              <div className="space-y-3" data-id="element-6448">
                <h4 className="text-xs font-bold text-white/30 uppercase tracking-widest" data-id="element-6449">
                  Información del Sistema
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4" data-id="element-6450">
                  <div className="flex items-center gap-3" data-id="element-6451">
                    <Calendar className="w-4 h-4 text-white/30" data-id="element-6452" />
                    <div data-id="element-6453">
                      <p className="text-xs text-white/40" data-id="element-6454">Fecha de registro</p>
                      <p className="text-sm font-medium" data-id="element-6455">
                        {formatDate(selectedUser.auth_created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6456">
                    <Clock className="w-4 h-4 text-white/30" data-id="element-6457" />
                    <div data-id="element-6458">
                      <p className="text-xs text-white/40" data-id="element-6459">Último acceso</p>
                      <p className="text-sm font-medium" data-id="element-6460">
                        {formatDate(selectedUser.last_sign_in_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-6461">
                    <Shield className="w-4 h-4 text-white/30" data-id="element-6462" />
                    <div className="min-w-0" data-id="element-6463">
                      <p className="text-xs text-white/40" data-id="element-6464">Auth ID</p>
                      <p className="text-xs font-mono text-white/70 truncate" data-id="element-6465">
                        {selectedUser.user_id}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}